import {
  Component,
  computed,
  inject,
  OnInit,
  signal
} from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { BaseAbstractPage, GeolocationService } from '@incust-mobile/core';
import { POSApiService, PosApiModel } from '@incust-mobile/api';
import { firstValueFrom, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {DecimalPipe} from "@angular/common";

const PAGE_SIZE = 20;

type SortMode = 'distance' | 'alphabet';

@Component({
  selector: 'app-pos-list-page',
  templateUrl: 'pos-list.page.html',
  styleUrls: ['pos-list.page.scss'],
  standalone: true,
  imports: [IonicModule, FormsModule, TranslatePipe, DecimalPipe]
})
export class PosListPage extends BaseAbstractPage implements OnInit {
  private posApi = inject(POSApiService);
  private geo = inject(GeolocationService);

  items = signal<PosApiModel[]>([]);
  isLoading = signal(false);
  hasMore = signal(true);
  sortMode = signal<SortMode>('distance');

  private offset = 0;
  private searchQuery = '';
  private searchSubject = new Subject<string>();

  readonly sortedItems = computed(() => {
    const list = this.items();
    const mode = this.sortMode();

    if (mode === 'alphabet') {
      return [...list].sort((a, b) =>
        (a.title ?? '').localeCompare(b.title ?? '')
      );
    }

    return [...list].sort((a, b) => {
      const da = a.latitude && a.longitude
        ? this.geo.distanceTo(a.latitude, a.longitude)
        : Infinity;
      const db = b.latitude && b.longitude
        ? this.geo.distanceTo(b.latitude, b.longitude)
        : Infinity;
      return da - db;
    });
  });

  constructor() {
    super();
    this.searchSubject
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe((q) => {
        this.searchQuery = q;
        this.reload();
      });
  }

  async ngOnInit() {
    await this.geo.requestAndWatch();
  }

  async ionViewWillEnter() {
    await this.load();
  }

  onSearch(event: any) {
    this.searchSubject.next(event.detail.value ?? '');
  }

  setSortMode($event: any) {
    const mode = $event.detail.value;
    if (mode === 'distance' || mode === 'alphabet') {
      this.sortMode.set(mode);
    }
  }

  async doRefresh(event: any) {
    await this.reload();
    event.target.complete();
  }

  async loadMore(event: any) {
    await this.load();
    event.target.complete();
  }

  getDistance(item: PosApiModel): number | null {
    if (!item.latitude || !item.longitude) return null;
    const d = this.geo.distanceTo(item.latitude, item.longitude);
    return d === Infinity ? null : d;
  }

  async goToMap() {
    await this.router.navigate(['/home/pos-map']);
  }

  async goToDetail(item: PosApiModel) {
    if (item.id) {
      await this.router.navigate(['/pos-detail', item.id]);
    }
  }

  private async reload() {
    this.offset = 0;
    this.items.set([]);
    this.hasMore.set(true);
    await this.load();
  }

  private async load() {
    if (!this.hasMore() || this.isLoading()) return;

    this.isLoading.set(true);
    const coords = this.geo.coords();

    try {
      const result = await firstValueFrom(
        this.posApi.incustControllersClientClientPos(
          undefined,
          coords?.lat,
          coords?.lng,
          undefined,
          undefined,
          this.searchQuery || undefined,
          undefined,
          undefined,
          undefined,
          1,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          PAGE_SIZE,
          this.offset
        )
      );

      const page = result ?? [];
      this.items.update((prev) => [...prev, ...page]);
      this.offset += page.length;
      this.hasMore.set(page.length === PAGE_SIZE);
    } catch (err) {
      await this.notificationService.show(
        'app#city_card#pos_list#load_error',
        2000,
        'top',
        'danger'
      );
    } finally {
      this.isLoading.set(false);
    }
  }
}
